import { Stack, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { StatGrid } from '@liminal/web-ui/display/StatGrid';
import type { Stat } from '@liminal/web-ui/display/StatGrid';
import { formatOrderDate } from '@liminal/utils/date';
import { AttributionSource } from '@/api/generated';
import { attributionSourceLabel } from '@/components/ui/statusHelpers';
import { UserIdLink } from '@/components/ui/UserIdLink';
import { getErrorMessage } from '@/errors/messages';
import { useReferralAttribution } from '@/hooks/useReferrals';

// How referrals were attributed across every inviter in the last 30 days
// (LIM-4840): how many each attribution path produced, and each referral
// with the path that attributed it. The PostHog funnel that goes with it is
// written up in product/referrals/README.md.

const COLUMNS = ['Attributed', 'Inviter', 'Invitee', 'Source', 'Qualified'] as const;

/** One source's tile: its count, or a placeholder while the counts load or after they fail. */
function sourceStat(source: AttributionSource, value: string, loading: boolean): Stat {
  return { id: source, label: attributionSourceLabel(source), value, tone: 'neutral', loading };
}

export function ReferralsPage() {
  const query = useReferralAttribution();
  const referrals = query.data?.referrals ?? [];
  const counts = query.data?.sourceCounts;

  // The server names every source, zero included; until it answers, each
  // source the app knows holds its place.
  const stats = counts
    ? counts.map((count) => sourceStat(count.source, String(count.referralCount), false))
    : Object.values(AttributionSource).map((source) => sourceStat(source, '—', query.isLoading));

  const errorMessage = query.isError ? `Could not load referrals: ${getErrorMessage(query.error)}` : '';

  return (
    <Stack spacing={3}>
      <Stack spacing={2}>
        <Typography variant="h6">By source, last 30 days</Typography>
        <StatGrid columns="auto" stats={stats} data-testid="referral-source-counts" />
      </Stack>

      <Card variant="elevated" padding="none">
        <Typography variant="h6" sx={{ px: 2, pt: 2, pb: 1 }}>
          Referrals, last 30 days
        </Typography>
        {query.data?.referralsTruncated ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
            Showing the most recent 500. The counts above cover every referral.
          </Typography>
        ) : null}
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          {/* Newest first, as the server orders them; there is no sort to apply. */}
          <DataTableBody
            columns={COLUMNS.length}
            isLoading={query.isLoading}
            isError={query.isError}
            isEmpty={referrals.length === 0}
            emptyMessage="No referrals in the last 30 days."
            errorMessage={errorMessage}
          >
            {referrals.map((referral) => (
              <TableRow key={referral.referralId} data-testid={`referral-${referral.referralId}`}>
                <TableCell>{formatOrderDate(referral.createTime)}</TableCell>
                <TableCell>
                  <UserIdLink userId={referral.referrerUserId}>{referral.referrerEmail || undefined}</UserIdLink>
                </TableCell>
                <TableCell>
                  <UserIdLink userId={referral.refereeUserId}>{referral.refereeEmail || undefined}</UserIdLink>
                </TableCell>
                <TableCell>{attributionSourceLabel(referral.source)}</TableCell>
                <TableCell>
                  <StatusPill
                    size="sm"
                    label={referral.qualified ? 'Qualified' : 'Not yet'}
                    tone={referral.qualified ? 'success' : 'neutral'}
                  />
                </TableCell>
              </TableRow>
            ))}
          </DataTableBody>
        </Table>
      </Card>
    </Stack>
  );
}
